/*
    Both the app and portal display statements + documents in a single list (overload the functions to handle both cases)
    This utility combines -> sorts -> outputs the display data necessary for the components
*/

import { minBy } from 'lodash';
import { DateTime } from 'luxon';

import {
  AppPaymentDueFragment,
  BorrowerDocumentAccess as AppBorrowerAccess,
  DocumentCategory,
  DocumentMailStatusMethod,
  DocumentMailStatusRecipientParty,
  DocumentType,
  LoanBillSettingsType,
  LoanDocumentDeliveryStatus,
} from '@willow/graphql-iso/src/app';
import { PortalPaymentDueFragment } from '@willow/graphql-iso/src/portal';
import {
  AuthorizedContactId,
  BorrowerId,
  DocumentId,
  EntityId,
  EscrowCompanyId,
  LoanEmailId,
  PaymentDueId,
  SideEffectId,
} from '@willow/types-iso';

import { getDisplayablePaymentsDue } from './currentPaymentDue';
import { DATE_FORMAT_SHORT_FULL_YEAR, dateFormat } from './dateFormat';
import { formatWorkoutPlanType } from './formatWorkoutPlan';

export const RevisedCallout = 'Replaced by revised copy';

export type DocumentMailStatusRecipient = {
  name: string;
  party?: DocumentMailStatusRecipientParty;
  borrowerId?: BorrowerId;
  authorizedContactId?: AuthorizedContactId;
  entityId?: EntityId;
  escrowCompanyId?: EscrowCompanyId;
};

export type DocumentMailStatus = {
  method: DocumentMailStatusMethod;
  date: string;
  testMode?: boolean;
  recipient?: DocumentMailStatusRecipient;
};

type DocumentMailStatusHistoryEntry = {
  method: DocumentMailStatusMethod;
  date: string | Date;
  testMode?: boolean | null;
  recipient?: {
    name: string;
    party?: DocumentMailStatusRecipientParty | null;
    borrowerId?: BorrowerId | null;
    authorizedContactId?: AuthorizedContactId | null;
    entityId?: EntityId | null;
    escrowCompanyId?: EscrowCompanyId | null;
  } | null;
};

// Reports the first action taken (e.g. a document tasked to mail and later mailed by working that
// task stays "tasked"), so re-mails and re-tasks don't move the status or the date
export const getDocumentMailStatus = (
  history: DocumentMailStatusHistoryEntry[] | undefined | null,
): DocumentMailStatus | undefined => {
  const original = minBy(history, (entry) => new Date(entry.date).getTime());
  if (!original) return undefined;

  const { recipient } = original;
  return {
    method: original.method,
    date: dateFormat(original.date, DATE_FORMAT_SHORT_FULL_YEAR),
    testMode: original.testMode ?? undefined,
    recipient: recipient
      ? {
          name: recipient.name,
          party: recipient.party ?? undefined,
          borrowerId: recipient.borrowerId ?? undefined,
          authorizedContactId: recipient.authorizedContactId ?? undefined,
          entityId: recipient.entityId ?? undefined,
          escrowCompanyId: recipient.escrowCompanyId ?? undefined,
        }
      : undefined,
  };
};

/* DEFINE TYPES FOR TABLE DISPLAY FORMATTING */
type BaseItem = {
  notificationType: 'document' | 'email' | 'manuallyUploaded';
  category?: DocumentCategory; // optional: the portal does not display categories
  title: string;
  subtitle?: string;
  callouts: string[];
  sortByDate: Date;
  createdAt: Date;
  version: number;
  date: Date | string; // date pre-sorted -> string formatted post-sorting
  borrowerAccess?: AppBorrowerAccess;
  isArchived: boolean;
  mailStatus?: DocumentMailStatus;
};

export type StatementItem = BaseItem & {
  id: PaymentDueId;
  documentType: 'Statement';
  statementDate: Date;
  billSettingType?: LoanBillSettingsType;
};

export type DocumentItem = BaseItem & {
  id: DocumentId;
  documentType: 'Document';
  documentSpecificationType: DocumentType;
  delivery?: { status: LoanDocumentDeliveryStatus; date?: string };
  externalRecipientName?: string;
};

export type EmailRecipientItem = {
  key: string;
  name: string;
  email?: string;
  emailSent: boolean;
  sentDate: string;
};

// One row per outbound email, grouped from the per-recipient copies that share a sideEffectId
export type EmailItem = BaseItem & {
  id: SideEffectId;
  documentType: 'Email';
  emailTemplate: string;
  // The primary borrower's copy when they received one, otherwise the earliest recipient's copy
  downloadLoanEmailId: LoanEmailId;
  recipients: EmailRecipientItem[];
};

export type DocumentDisplayItem = StatementItem | DocumentItem | EmailItem;

/* HELPER FUNCTIONS FOR FORMATTING TABLE ITEMS (APP + PORTAL OVERLOADED */
export function getStatementItems(
  paymentsDue: AppPaymentDueFragment[],
  transferDate: Date | undefined,
  interestDueAtPayoff: boolean,
): DocumentDisplayItem[];
export function getStatementItems(
  paymentsDue: PortalPaymentDueFragment[],
  transferDate: Date | undefined,
  interestDueAtPayoff: boolean,
): DocumentDisplayItem[];
export function getStatementItems(
  paymentsDue: AppPaymentDueFragment[] | PortalPaymentDueFragment[],
  transferDate: Date | undefined,
  interestDueAtPayoff: boolean,
): DocumentDisplayItem[] {
  // Filter out statements that we don't expect to collect on
  const collectablePayments = getDisplayablePaymentsDue(paymentsDue, transferDate, interestDueAtPayoff);

  const mappedStatements = collectablePayments.flatMap((paymentDue) => {
    const { id, statementDate, paymentDate, status } = paymentDue;
    const statements = paymentDue.statements.map((s, index) => {
      return { ...s, isOutdated: index < paymentDue.statements.length - 1 };
    });

    // Flatten statements (there may be multiple per paymentDue if there are revisions)
    return statements?.flat().map((statement) => {
      const callouts: string[] = [];
      const { createdAt, version, isOutdated } = statement;
      isOutdated && callouts.push(RevisedCallout);

      if (status.workoutPlanType) {
        callouts.push(formatWorkoutPlanType(status.workoutPlanType));
      }
      const statementSentDate = getStatementSentDate(createdAt, statementDate);

      return {
        id,
        notificationType: 'document' as const,
        category: DocumentCategory.Statement,
        documentType: 'Statement' as const,
        title: `Statement (${dateFormat(paymentDate, 'LLL yyyy')})`,
        callouts,
        sortByDate: new Date(statementDate),
        statementDate: new Date(statementDate),
        createdAt: new Date(createdAt),
        date: statementSentDate,
        version,
        borrowerAccess: 'borrowerAccess' in statement ? statement.borrowerAccess : undefined, // Shared component, but portal does not have borrowerAccess
        isArchived: isOutdated,
        // Shared component, but portal statements don't expose mail status or who they were billed to
        mailStatus: getDocumentMailStatus('mailStatusHistory' in statement ? statement.mailStatusHistory : undefined),
        billSettingType: 'billSettingType' in paymentDue ? paymentDue.billSettingType : undefined,
      };
    });
  });

  return mappedStatements;
}

const getStatementSentDate = (createdAt: any, statementDate: any) => {
  createdAt = new Date(createdAt);
  statementDate = new Date(statementDate);

  return DateTime.max(DateTime.fromJSDate(createdAt), DateTime.fromJSDate(statementDate)).toJSDate();
};

export const sortDocumentsByDate = (documents: DocumentDisplayItem[]): DocumentDisplayItem[] => {
  // Sorted by createdAt date (newest documents display first)
  const sortedByDate = documents.sort((a, b) => {
    const dateA = new Date(a.sortByDate).getTime();
    const dateB = new Date(b.sortByDate).getTime();

    if (dateA === dateB) {
      return b.version > a.version ? 1 : -1;
    }

    // Return most recent date first
    return dateB > dateA ? 1 : -1;
  });

  return sortedByDate;
};
